import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { groupBy } from 'lodash'
import { usePostHog } from 'posthog-js/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'

// Redux
import {
  selectCurrentDocument,
  selectCurrentProject,
  selectSupplementaryConditionsAreProcessing,
} from '../../../../redux/application-slice'
import {
  useGetDocumentsListByProjectQuery,
  useGetUserProfileQuery,
} from '../../../../redux/api-slice'
import {
  useGetProjectRiskQuery,
  useGetProjectRisksProcessingStatusQuery,
  useGetRiskReviewSectionsQuery,
} from '../../../../redux/api/project-risk-api-slice'
import { useGetRiskCategoriesQuery } from '../../../../redux/api/qa-api-slice'
import {
  selectRiskListSearchQuery,
  selectSelectedCategoryForRiskList,
  selectSelectedDefaultPositionForRiskList,
  selectSelectedDocuments,
  selectSelectedRankingForRiskList,
  selectSelectedRiskList,
  selectSelectedSort,
  selectSelectedStatusFilter,
  setSelectedCategoryFilter,
  selectSelectedConflictsFilter,
} from '../../../../redux/risks-search-slice'

// Components
import RiskReviewCard from '../risk-review-card/risk-review-card'
import { WorkflowFilter } from '../../workflow-components/filter-display'
import RiskReviewSectionNavigation from './risk-review-section-navigation'

// Mantine components
import {
  Skeleton,
  Tooltip as MantineTooltip,
  Text,
  Collapse,
  ActionIcon,
} from '@mantine/core'
import {
  ProjectRisk,
  RiskReviewSection,
} from '../../../../shared/interfaces/project/risk/risk-inteface'
import { useNavigate } from 'react-router-dom'
// Third-party
import { Tooltip } from 'react-tooltip'
import { RiskListHeader } from './risk-list-header'
import { RiskListTemplateBanner } from '../../../banners/specific-banners'
import {
  SelectedSource,
  setNavigatedSectionSources,
} from '../../../../redux/viewer-slice'
import { POSTHOG } from '../../../../utils/posthog-constants'

const mandatoryRiskCategoriesDisplayNamesToKeys = {
  'Consequential Damages': 'CONSEQUENTIAL_DAMAGES',
  Delays: 'DELAYS',
  'Force Majeure': 'FORCE_MAJEURE',
  Indemnification: 'INDEMNIFICATION',
  Insurance: 'INSURANCE',
  'Limit of Liability': 'LIMIT_OF_LIABILITY',
  'Liquidated Damages': 'LIQUIDATED_DAMAGES',
  Notice: 'NOTICE',
  Payment: 'PAYMENT',
  'Performance Security': 'PERFORMANCE_SECURITY',
  Termination: 'TERMINATION',
  Warranty: 'WARRANTY',
}

export const useCategoryIdToNameMapping = ({
  riskCategoryId,
}: {
  riskCategoryId: string
}): string => {
  const currentProject = useSelector(selectCurrentProject)
  // TODO put this payload in Redux, so we dont have to fetch it every time a category is displayed (ouch)
  const { data: riskCategories } = useGetRiskCategoriesQuery(
    currentProject?.uuid ?? ''
  )

  if (!riskCategories) {
    return ''
  }

  const topLevelMapping = riskCategories.reduce(
    (acc, category) => {
      if (category.id && category.name) {
        acc[category.id] = category.name
      }
      return acc
    },
    {} as Record<string, string>
  )
  return topLevelMapping[riskCategoryId]
}

const RiskReviewPage: React.FC = () => {
  const currentProject = useSelector(selectCurrentProject)
  const riskListSearchQuery = useSelector(selectRiskListSearchQuery)
  const selectedDocuments = useSelector(selectSelectedDocuments)
  const currentDocument = useSelector(selectCurrentDocument)
  const dispatch = useDispatch()
  const posthog = usePostHog()
  const navigation = useNavigate()

  const { data: user } = useGetUserProfileQuery(undefined)

  const isCategoryValid = (category: string | null) =>
    category !== '' && category !== null && category !== 'No Section'

  const supplementaryConditionsAreProcessing = useSelector(
    selectSupplementaryConditionsAreProcessing
  )

  const [shouldDocumentPoll, setShouldDocumentPoll] = useState(true)

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set()
  )

  const toggleCategoryCollapse = useCallback((category: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(category)) {
        newSet.delete(category)
      } else {
        newSet.add(category)
      }
      return newSet
    })
  }, [])

  const { currentData: documents } = useGetDocumentsListByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken,
    {
      pollingInterval: shouldDocumentPoll ? 10000 : 0,
      refetchOnMountOrArgChange: true,
    }
  )

  const documentProcessingCount = useMemo(() => {
    if (!documents) {
      return 0
    }

    return documents.filter(
      (document) =>
        document.job_status === 'PROCESSING' ||
        document.job_status === 'PENDING'
    ).length
  }, [documents])

  // isFirstTimeProcessing is true when the document processing count is greater than 0. it models
  // the scenario where a user has just created a project, uploaded a document, and is watching the processing state tick down.
  // Because our document processing & risk processing queues are async, we need this to bridge these two states, when risks haven't started processing yet (but are about to).
  const [isFirstTimeProcessing, setIsFirstTimeProcessing] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionTimer, setTransitionTimer] = useState<NodeJS.Timeout | null>(
    null
  )

  useEffect(() => {
    if (documentProcessingCount > 0) {
      setIsFirstTimeProcessing(true)
    } else if (documentProcessingCount === 0 && isFirstTimeProcessing) {
      // Start a transition period when document processing ends
      setShouldDocumentPoll(false)
      setIsTransitioning(true)
      // Set a timeout to end the transition period if risk processing doesn't start
      const timer = setTimeout(() => {
        setIsTransitioning(false)
        setIsFirstTimeProcessing(false)
      }, 10000)
      setTransitionTimer(timer)

      // Ensure the last timer we make is cleaned up
      return () => {
        if (transitionTimer) clearTimeout(transitionTimer)
      }
    }
  }, [documentProcessingCount, isFirstTimeProcessing, transitionTimer])

  const [filter, setFilter] = useState<WorkflowFilter>({})

  const [riskPollingInterval, setRiskPollingInterval] = useState<
    number | undefined
  >(undefined)

  const { currentData: riskPipelineStats } =
    useGetProjectRisksProcessingStatusQuery(currentProject?.uuid ?? skipToken, {
      pollingInterval: 2000,
    })

  const {
    data: projectRisks,
    refetch,
    isUninitialized,
    isSuccess,
    isLoading,
  } = useGetProjectRiskQuery(
    currentProject?.uuid
      ? {
          projectUUID: currentProject.uuid,
        }
      : skipToken,
    {
      pollingInterval: riskPollingInterval,
    }
  )

  useEffect(() => {
    if (riskPipelineStats?.count && riskPipelineStats.count > 0) {
      // Risk processing has started, maintain the transitioning state
      setIsTransitioning(true)
      // Clear any existing transition timer
      if (transitionTimer) {
        clearTimeout(transitionTimer)
        setTransitionTimer(null)
      }
    } else if (riskPipelineStats?.count === 0 && isTransitioning) {
      // Risk processing has ended, but we'll delay ending the transitioning state
      const timer = setTimeout(() => {
        setIsTransitioning(false)
        setIsFirstTimeProcessing(false)

        // Only attempt to refetch if:
        // 1. We have a valid project UUID
        // 2. The query has been initialized (not in uninitialized state)
        // 3. The query is not currently loading
        // 4. The query has successfully completed at least once
        if (
          currentProject?.uuid &&
          !isUninitialized &&
          !isLoading &&
          isSuccess
        ) {
          refetch()
        }
      }, 2000)
      setTransitionTimer(timer)
    }
  }, [
    riskPipelineStats?.count,
    isTransitioning,
    refetch,
    transitionTimer,
    currentProject?.uuid,
    isUninitialized,
    isSuccess,
    isLoading,
  ])

  // Ensure all timers are cleaned up.
  useEffect(() => {
    return () => {
      if (transitionTimer) clearTimeout(transitionTimer)
    }
  }, [transitionTimer])

  useEffect(() => {
    if (!projectRisks || projectRisks?.some((r) => r.processing_status === 0)) {
      setRiskPollingInterval(5000)
    } else {
      setRiskPollingInterval(undefined)
    }
  }, [projectRisks])

  const [searchParams] = useSearchParams()
  const commentUUID = searchParams.get('comment')

  const selectedCategory = useSelector(selectSelectedCategoryForRiskList)
  const selectedRanking = useSelector(selectSelectedRankingForRiskList)
  const selectedRiskList = useSelector(selectSelectedRiskList)
  const selectedDefaultPositionFilter = useSelector(
    selectSelectedDefaultPositionForRiskList
  )
  const selectedStatusFilter = useSelector(selectSelectedStatusFilter)
  const selectedSort = useSelector(selectSelectedSort)
  const selectedConflictsFilter = useSelector(selectSelectedConflictsFilter)
  useEffect(() => {
    if (!commentUUID) {
      return
    }
    setFilter((f) => {
      return { ...f, comment_uuid: commentUUID }
    })
  }, [commentUUID])

  const categoryEntries = useMemo(
    () =>
      Object.entries(mandatoryRiskCategoriesDisplayNamesToKeys).map(
        ([name, key]) => ({ name, key })
      ),
    []
  )

  /* For navigating the risk review sections */
  const [sectionIndexes, setSectionIndexes] = useState(
    Object.fromEntries(
      Object.keys(mandatoryRiskCategoriesDisplayNamesToKeys).map((key) => [
        mandatoryRiskCategoriesDisplayNamesToKeys[key],
        -1,
      ])
    )
  )
  const [sectionForNavigation, setSectionForNavigation] = useState<
    string | null
  >(null)

  useEffect(() => {
    dispatch(setNavigatedSectionSources([]))
    setSectionIndexes(
      Object.fromEntries(
        Object.keys(mandatoryRiskCategoriesDisplayNamesToKeys).map((key) => [
          mandatoryRiskCategoriesDisplayNamesToKeys[key],
          -1,
        ])
      )
    )
    setSectionForNavigation(null)
  }, [dispatch, currentProject?.uuid])

  const riskReviewSectionsResults = categoryEntries.map(({ key }) =>
    //eslint-disable-next-line react-hooks/rules-of-hooks
    useGetRiskReviewSectionsQuery(
      user?.feature_flags?.risk_review_section && currentProject?.uuid
        ? {
            projectUUID: currentProject.uuid,
            sectionType: key,
          }
        : skipToken
    )
  )

  const reviewSectionQueryLoading = useMemo(() => {
    return riskReviewSectionsResults.some(
      (result) => result.isLoading || result.isFetching
    )
  }, [riskReviewSectionsResults])

  // 2. Memoize the post-processing step
  const reviewSectionHolders = useMemo(() => {
    if (!user?.feature_flags?.risk_review_section || !currentProject?.uuid) {
      return []
    }
    const maxScoreBySectionContentSegment: {
      [key: number]: { score: number; section: string }
    } = {}

    categoryEntries.forEach(({ key }, idx) => {
      const riskReviewSections = riskReviewSectionsResults[idx].data
      riskReviewSections?.forEach((section) => {
        if (!section.content_segment.id) {
          return
        }
        const segmentId = section.content_segment.id
        const maxScore = section.composite_score
        if (
          !maxScoreBySectionContentSegment[segmentId] ||
          maxScoreBySectionContentSegment[segmentId].score < maxScore
        ) {
          maxScoreBySectionContentSegment[segmentId] = {
            score: maxScore,
            section: section.section_type,
          }
        }
      })
    })

    const TOLERANCE = 0.1
    const rawSections: { key: string; sectionPages: RiskReviewSection[] }[] = []
    riskReviewSectionsResults.forEach((riskReviewSectionsResult, idx) => {
      const riskReviewSections = riskReviewSectionsResult.data
      if (riskReviewSections && riskReviewSections.length > 0) {
        const sectionType = categoryEntries[idx].key
        const finalRiskReviewSections: RiskReviewSection[] = []
        riskReviewSections.forEach((riskReviewSection) => {
          if (!riskReviewSection.content_segment.id) return
          const segmentId = riskReviewSection.content_segment.id
          if (
            Math.abs(
              riskReviewSection.composite_score -
                maxScoreBySectionContentSegment[segmentId].score
            ) < TOLERANCE
          ) {
            finalRiskReviewSections.push(riskReviewSection)
          }
        })
        rawSections.push({
          key: sectionType,
          sectionPages: finalRiskReviewSections,
        })
      }
    })

    if (rawSections.length === 0) {
      return []
    }
    return categoryEntries
      .map(({ key }) => {
        const rawSection = rawSections.find((section) => section.key === key)
        if (!rawSection) {
          return undefined
        }
        const riskReviewSections = rawSection.sectionPages
        const groupedPagedRisks: { [key: number]: RiskReviewSection[] } =
          groupBy(riskReviewSections, 'page')
        const processedPages: number[] = []
        const rankedRiskReviewSectionPages = riskReviewSections
          ?.map((section) => {
            const page = section.page
            if (processedPages.includes(page)) {
              return null
            }
            processedPages.push(page)
            return groupedPagedRisks[page]
          })
          .filter((section) => section !== null)

        return {
          key: key,
          sectionPages: rankedRiskReviewSectionPages,
        }
      })
      .filter(
        (item): item is { key: string; sectionPages: RiskReviewSection[][] } =>
          item !== undefined
      )
  }, [
    categoryEntries,
    riskReviewSectionsResults,
    currentProject?.uuid,
    user?.feature_flags?.risk_review_section,
  ])

  useEffect(() => {
    if (sectionForNavigation) {
      const selectedSectionHolders = reviewSectionHolders?.filter(
        (sectionHolder) =>
          sectionHolder && sectionHolder.key === sectionForNavigation
      )
      if (!selectedSectionHolders) return
      selectedSectionHolders.forEach((sectionHolder) => {
        if (!sectionHolder) return
        const sectionIndex = sectionIndexes[sectionHolder.key] || 0
        const sectionGroupPage = sectionHolder.sectionPages?.[sectionIndex]
        if (!sectionGroupPage) {
          return
        }

        const sectionSources: SelectedSource[] = []

        sectionGroupPage.forEach((sectionPage: RiskReviewSection) => {
          const headerSegment = sectionPage.header_segment
          const contentSegment = sectionPage.content_segment
          if (!headerSegment?.quads && !contentSegment?.quads) {
            return
          }
          if (
            contentSegment.document_uuid !== undefined &&
            contentSegment.quads?.length
          ) {
            sectionSources.push({
              quads: contentSegment.quads,
              page: sectionPage.page,
              id: '' + contentSegment.id,
              isPrimary: false,
              documentUUID: contentSegment.document_uuid,
            })
          }
          if (
            headerSegment?.document_uuid !== undefined &&
            headerSegment?.quads?.length
          ) {
            sectionSources.push({
              quads: headerSegment.quads,
              page: sectionPage.page,
              id: '' + headerSegment.id,
              isPrimary: true,
              documentUUID: headerSegment.document_uuid,
            })
          }
        })
        if (sectionSources.length) {
          // Deduplicate by quads
          const uniqueSectionSources = uniqueByQuads(sectionSources)
          const page = uniqueSectionSources[0].page.toString()
          navigation(
            `/${currentProject?.uuid}/riskreview/${uniqueSectionSources[0].documentUUID}?page=${page}`
          )
          dispatch(setNavigatedSectionSources(uniqueSectionSources))
        }
      })
    }
  }, [sectionForNavigation, dispatch, sectionIndexes])

  // Utility to get unique section sources by quads
  function uniqueByQuads<T extends { quads: any }>(arr: T[]): T[] {
    const seen = new Set()
    return arr.filter((item) => {
      const quadsKey = JSON.stringify(item.quads)
      if (seen.has(quadsKey)) return false
      seen.add(quadsKey)
      return true
    })
  }

  const filteredRisks = useMemo(() => {
    let risks: ProjectRisk[] = []
    if (!projectRisks) {
      return []
    }
    if (!selectedDocuments?.length) {
      risks = projectRisks
    } else {
      risks = projectRisks?.reduce<ProjectRisk[]>((acc, cur) => {
        const doc_uuids = cur.sources?.map((c) => c.document_segment.document)
        const relevantDocUUIDs = selectedDocuments
          ?.filter((d) => doc_uuids?.includes(d.uuid))
          .map((d) => d.uuid)
        if (relevantDocUUIDs?.length) {
          const filteredSources = cur.sources?.filter((s) =>
            relevantDocUUIDs.includes(
              s.document_segment.document?.toString() ?? ''
            )
          )
          const filteredRisk = {
            ...cur,
            sources: filteredSources,
          }
          return [...acc, filteredRisk]
        }
        return acc
      }, [])
    }

    return risks
  }, [projectRisks, selectedDocuments])

  const risksToDisplay = useMemo(() => {
    if (filter?.comment_uuid) {
      return filteredRisks?.filter((r) => {
        return r?.comments?.some((c) => c?.id === filter.comment_uuid)
      })
    }
    let risks = filteredRisks
    if (selectedRiskList) {
      risks = risks?.filter((r) => {
        const riskListIds = r.risk_lists?.map((rl) => rl.risk_list_template)
        return riskListIds?.includes(selectedRiskList?.id)
      })
    }
    if (selectedStatusFilter) {
      risks = risks?.filter((r) => {
        return r?.status === parseInt(selectedStatusFilter)
      })
    }
    if (selectedCategory !== '' && selectedCategory !== null) {
      risks = risks?.filter((r) => {
        if (selectedCategory === 'null') {
          return r?.risk_category === null
        }
        return r?.risk_category?.id === selectedCategory
      })
    }
    if (riskListSearchQuery) {
      risks = risks?.filter((r) => {
        return r?.risk_name
          ?.toLowerCase()
          .includes(riskListSearchQuery.toLowerCase())
      })
    }
    if (selectedRanking) {
      risks = risks?.filter((r) => {
        return r?.risk_ranking === parseInt(selectedRanking)
      })
    }
    if (selectedDefaultPositionFilter) {
      risks = risks?.filter((r) => {
        return (
          (r?.default_position_met === true &&
            selectedDefaultPositionFilter === 'MET') ||
          (r?.default_position_met === false &&
            selectedDefaultPositionFilter === 'NOT_MET') ||
          (r?.default_position_met === null &&
            r.default_position_explanation &&
            selectedDefaultPositionFilter === 'INDETERMINATE')
        )
      })
    }
    if (selectedConflictsFilter) {
      risks = risks?.filter((r) => {
        return r?.has_conflicts === (selectedConflictsFilter === 'true')
      })
    }

    return risks
  }, [
    selectedRiskList,
    riskListSearchQuery,
    filter,
    filteredRisks,
    selectedCategory,
    selectedRanking,
    selectedDefaultPositionFilter,
    selectedStatusFilter,
    selectedConflictsFilter,
  ])

  const groupedRisks = useMemo(() => {
    const grouped = groupBy(
      risksToDisplay,
      (risk) => risk.risk_category?.name || 'No Section'
    )

    if (selectedCategory === null) {
      Object.keys(mandatoryRiskCategoriesDisplayNamesToKeys).forEach(
        (category) => {
          const selectedSectionHolders = reviewSectionHolders?.filter(
            (sectionHolder) =>
              sectionHolder &&
              sectionHolder.key ===
                mandatoryRiskCategoriesDisplayNamesToKeys[category]
          )
          if (!selectedSectionHolders?.length) {
            return
          }
          if (user?.feature_flags?.risk_review_section && !grouped[category]) {
            grouped[category] = []
          }
        }
      )
    }

    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      // Always put "No Section" at the top
      if (a === 'No Section') return -1
      if (b === 'No Section') return 1
      // Otherwise sort alphabetically
      return a.localeCompare(b)
    })

    const sortRisks = (risks: ProjectRisk[], sortType: string) => {
      const risksCopy = [...risks]
      switch (sortType) {
        case 'AZ':
          return risksCopy.sort(
            (a, b) => a.risk_name?.localeCompare(b.risk_name ?? '') ?? 0
          )
        case 'ZA':
          return risksCopy.sort(
            (a, b) => b.risk_name?.localeCompare(a.risk_name ?? '') ?? 0
          )
        case 'HIGH_TO_LOW':
          return risksCopy.sort(
            (a, b) => (b.risk_ranking || 0) - (a.risk_ranking || 0)
          )
        case 'LOW_TO_HIGH':
          return risksCopy.sort(
            (a, b) => (a.risk_ranking || 0) - (b.risk_ranking || 0)
          )
        default:
          return risksCopy
      }
    }

    const sortedGrouped = sortedCategories.reduce((acc, category) => {
      acc[category] = sortRisks(grouped[category], selectedSort)
      return acc
    }, {})

    return sortedGrouped
  }, [
    risksToDisplay,
    selectedSort,
    selectedCategory,
    user?.feature_flags?.risk_review_section,
    reviewSectionHolders,
  ])

  const isRiskPresentForCategory = (category: string) => {
    return groupedRisks[category].length > 0
  }

  const navigateSection = useCallback(
    (sectionKey: string, forward: boolean) => {
      dispatch(setNavigatedSectionSources([]))
      const sectionIndex = sectionIndexes[sectionKey]
      const sectionHolder = reviewSectionHolders.find(
        (r) => r.key === sectionKey
      )
      if (
        !sectionKey ||
        sectionIndex === undefined ||
        !sectionHolder ||
        !sectionHolder.sectionPages ||
        sectionHolder.sectionPages.length == 0
      )
        return

      if (sectionIndex === -1) {
        posthog?.capture(POSTHOG.risk_review_section_started, {
          category: sectionKey,
          project_uuid: currentProject?.uuid,
          document_uuid: currentDocument?.uuid,
        })
      }
      const updatedSectionIndexes = { ...sectionIndexes }
      if (forward) {
        updatedSectionIndexes[sectionKey] = Math.min(
          sectionIndex + 1,
          sectionHolder.sectionPages.length - 1
        )
        posthog?.capture(POSTHOG.risk_review_section_navigated, {
          sectionKey,
          project_uuid: currentProject?.uuid,
          document_uuid: currentDocument?.uuid,
        })
      } else {
        updatedSectionIndexes[sectionKey] = Math.max(sectionIndex - 1, 0)
      }
      setSectionIndexes(updatedSectionIndexes)
    },
    [dispatch, sectionIndexes, reviewSectionHolders]
  )

  const ProjectRisksLoading = () => (
    <div className="flex h-full w-full flex-col items-center justify-center py-4 pr-6">
      <div className="h-full w-full space-y-3">
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={index}
            h="112.55px"
            w="100%"
            radius="md"
            className="!border !border-gray-300"
          />
        ))}
      </div>
    </div>
  )

  return (
    <div className="flex h-full flex-col bg-[#e5e7eb]">
      <div className="mr-1 flex flex-col overflow-x-hidden border-b border-gray-300">
        <div className="w-full space-y-2 p-4">
          <RiskListHeader />
        </div>
      </div>
      <div className="w-full">
        <RiskListTemplateBanner />
      </div>
      <div className="scrollbar-default scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="w-full space-y-2 px-4 py-2 pr-5">
          {projectRisks === undefined ? (
            <ProjectRisksLoading />
          ) : (
            <div className="mb-24 flex flex-col space-y-6">
              {Object.keys(groupedRisks).length === 0 ? (
                <div className="flex h-32 items-center justify-center text-gray-500">
                  No risks match these filters
                </div>
              ) : (
                Object.entries(groupedRisks).map(([category, risks]) => (
                  <div key={category} className="space-y-3">
                    <div className="mb-5 mt-2 flex items-center justify-between text-gray-600">
                      <div className="flex flex-grow items-center">
                        <Collapse
                          in={true}
                          transitionDuration={500}
                          transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
                        >
                          <ActionIcon
                            hidden={collapsedCategories.has(category)}
                            variant="subtle"
                            onClick={() => toggleCategoryCollapse(category)}
                          >
                            <ChevronUpIcon className="h-4 w-4" />
                          </ActionIcon>
                          <ActionIcon
                            hidden={!collapsedCategories.has(category)}
                            variant="subtle"
                            onClick={() => toggleCategoryCollapse(category)}
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </ActionIcon>
                        </Collapse>
                        <MantineTooltip
                          label={`See all ${category ?? ''} related risks`}
                          position="right"
                          offset={10}
                          disabled={!isCategoryValid(category)}
                        >
                          <Text
                            size="md"
                            fw={500}
                            ml="sm"
                            inherit
                            className={`${
                              isCategoryValid(category) &&
                              isRiskPresentForCategory(category)
                                ? 'cursor-pointer transition-colors hover:text-blue-500 hover:underline'
                                : ''
                            } ${
                              mandatoryRiskCategoriesDisplayNamesToKeys[
                                category
                              ] === sectionForNavigation
                                ? 'font-bold text-blue-500'
                                : ''
                            }`}
                            onClick={() => {
                              if (isCategoryValid(category)) {
                                dispatch(
                                  setSelectedCategoryFilter(
                                    risks?.[0]?.risk_category?.id ?? null
                                  )
                                )
                              }
                            }}
                          >
                            {category}
                          </Text>
                        </MantineTooltip>
                        <div className="mx-4 flex-grow">
                          <hr className="border-t border-gray-300" />
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <RiskReviewSectionNavigation
                            category={category}
                            mandatoryRiskCategoriesDisplayNamesToKeys={
                              mandatoryRiskCategoriesDisplayNamesToKeys
                            }
                            reviewSectionQueryLoading={
                              reviewSectionQueryLoading
                            }
                            sectionIndexes={sectionIndexes}
                            reviewSectionHolders={reviewSectionHolders}
                            navigateSection={navigateSection}
                            setSectionForNavigation={setSectionForNavigation}
                            userFeatureFlags={user?.feature_flags}
                          />
                        </div>
                      </div>
                    </div>
                    {Array.isArray(risks) &&
                      !collapsedCategories.has(category) &&
                      risks.map((risk) => (
                        <RiskReviewCard
                          key={risk.id}
                          documents={documents ?? []}
                          data-testid={risk.id}
                          projectRisk={risk}
                        />
                      ))}
                    {Array.isArray(risks) &&
                      risks.length === 0 &&
                      !collapsedCategories.has(category) && (
                        <div className="flex h-10 items-center justify-center text-gray-500">
                          No risks found in this section
                        </div>
                      )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      <Tooltip id={'risk-info-id'} style={{ zIndex: 100 }} />
    </div>
  )
}

export default RiskReviewPage
